<template>
  <div class="login-page">
    <!-- Animated Background -->
    <div class="background-animation">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>

    <div class="login-container">
      <!-- Left Panel - Branding -->
      <div class="branding-panel">
        <div class="branding-content">
          <img class="" src="../../assets/logo.png" alt="logo"/>
          <p class="brand-tagline">Empowering Ideas, Funding the Future</p>
          <div class="features">
            <div class="feature-item">
              <span class="feature-icon">🔒</span>
              <span>Secure Access</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">⚡</span>
              <span>Real-time Analytics</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">📊</span>
              <span>Advanced Dashboard</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel - Login Form -->
      <div class="form-panel">
        <div class="form-wrapper">
          <div class="form-header">
            <h2>Welcome Back</h2>
            <p>Sign in to access the admin panel</p>
          </div>

          <!-- Error Alert -->
          <transition name="shake">
            <div class="error-alert" v-if="errorLogin">
              <div class="error-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <span>{{ errorMessage }}</span>
              <button class="error-close" @click="errorLogin = false">×</button>
            </div>
          </transition>

          <!-- Email Input -->
          <div class="input-group" :class="{ 'focused': emailFocused, 'has-value': form_login.username }">
            <label for="email">Email Address</label>
            <div class="input-wrapper">
              <span class="input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </span>
              <input 
                id="email"
                type="email" 
                v-model="form_login.username" 
                @focus="emailFocused = true"
                @blur="emailFocused = false"
                @keypress="errorLogin = false"
                @keyup.enter="login"
                autocomplete="email"
              />
            </div>
          </div>

          <!-- Password Input -->
          <div class="input-group" :class="{ 'focused': passwordFocused, 'has-value': form_login.password }">
            <label for="password">Password</label>
            <div class="input-wrapper">
              <span class="input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </span>
              <input 
                id="password"
                :type="showPassword ? 'text' : 'password'" 
                v-model="form_login.password"
                @focus="passwordFocused = true"
                @blur="passwordFocused = false"
                @keypress="errorLogin = false"
                @keyup.enter="login"
                autocomplete="current-password"
              />
              <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <!-- Remember Me & Forgot Password -->
          <div class="form-options">
            <label class="remember-me">
              <input type="checkbox" v-model="rememberMe" />
              <span class="checkmark"></span>
              <span>Remember me</span>
            </label>
          </div>

          <!-- Login Button -->
          <button 
            class="login-btn" 
            @click="login" 
            :disabled="loader"
            :class="{ 'loading': loader }"
          >
            <span v-if="!loader">Sign In</span>
            <span v-else class="loader-wrapper">
              <span class="spinner"></span>
              <span>Signing in...</span>
            </span>
          </button>
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <p>© 2025 IdeasFundX. All rights reserved.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Company from "@/store/services/admin/company";
import Admin from "@/store/services/admin/admin";

export default {
  name: "Login",

  data() {
    return {
      errorLogin: false,
      errorMessage: '',
      loader: false,
      form_login: {},
      log: {
        id: null,
        profile: null,
      },
      emailFocused: false,
      passwordFocused: false,
      showPassword: false,
      rememberMe: false,
    };
  },

  mounted() {
    // Check for remembered credentials
    const remembered = localStorage.getItem('rememberedEmail');
    if (remembered) {
      this.form_login.username = remembered;
      this.rememberMe = true;
    }
  },

  methods: {
    saveLogs(userId, profile) {
      this.log.id = userId;
      this.log.profile = profile;
      Admin.storeLogs(this.log).then((res) => {
        if (res.status) {
          console.log(res);
        }
      });
    },

    login() {
      this.loader = true;
      
      if (!this.form_login.username) {
        this.errorLogin = true;
        this.errorMessage = 'Please provide a valid email';
        this.loader = false;
        return;
      }
      
      if (!this.form_login.password) {
        this.errorLogin = true;
        this.errorMessage = 'Please provide a valid password';
        this.loader = false;
        return;
      }

      // Handle remember me
      if (this.rememberMe) {
        localStorage.setItem('rememberedEmail', this.form_login.username);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      Company.login(JSON.stringify(this.form_login)).then((res) => {
        if (res.status == 200) {
          localStorage.profile = JSON.stringify(res.data);
          this.$router.push('/admin/company');
          this.saveLogs(res.data.user_id, 'Admin');
        } else {
          this.errorLogin = true;
          this.errorMessage = res.data.message;
          this.loader = false;
        }
      });
    },
  },
};
</script>

<style scoped>
/* Reset & Base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  /* background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); */
  position: relative;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Animated Background */
.background-animation {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: float 20s infinite ease-in-out;
}

.orb-1 {
  width: 600px;
  height: 600px;
  /* background: linear-gradient(135deg, #00aac3, #0891b2); */
  top: -200px;
  left: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  /* background: linear-gradient(135deg, #6366f1, #8b5cf6); */
  bottom: -150px;
  right: -100px;
  animation-delay: -7s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #14b8a6, #10b981);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -30px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(-30px, -20px) scale(1.02); }
}

/* Login Container */
.login-container {
  display: flex;
  width: 100%;
  max-width: 1000px;
  min-height: 600px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin: 20px;
  position: relative;
  z-index: 1;
}

/* Branding Panel */
.branding-panel {
  flex: 1;
  background: #ffffff;
  padding: 60px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.branding-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300aac3' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.branding-content {
  position: relative;
  z-index: 1;
  text-align: center;
  color: #1e293b;
}

.logo {
  width: 80px;
  height: auto;
  margin-bottom: 20px;
  filter: brightness(0) invert(1);
}

.brand-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}

.brand-tagline {
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 40px;
}

.features {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 1rem;
  padding: 12px 20px;
  background: rgba(0, 170, 195, 0.1);
  border-radius: 12px;
  color: #1e293b;
}

.feature-icon {
  font-size: 1.2rem;
}

/* Form Panel */
.form-panel {
  flex: 1;
  padding: 60px 50px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #0f172a;
}

.form-wrapper {
  max-width: 360px;
  margin: 0 auto;
  width: 100%;
}

.form-header {
  margin-bottom: 40px;
  text-align: center;
}

.form-header h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 8px;
}

.form-header p {
  color: #94a3b8;
  font-size: 1rem;
}

/* Error Alert */
.error-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #fca5a5;
  margin-bottom: 24px;
  font-size: 0.9rem;
}

.error-icon {
  flex-shrink: 0;
  color: #ef4444;
}

.error-close {
  margin-left: auto;
  background: none;
  border: none;
  color: #fca5a5;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.shake-enter-active {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }
}

/* Input Groups */
.input-group {
  margin-bottom: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.input-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 8px;
  transition: color 0.2s ease;
  text-align: left;
}

.input-group.focused label {
  color: #00aac3;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 16px;
  color: #64748b;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
}

.input-group.focused .input-icon {
  color: #00aac3;
}

.input-wrapper input {
  width: 100%;
  padding: 16px 16px 16px 52px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  color: #ffffff;
  transition: all 0.2s ease;
  outline: none;
}

.input-wrapper input::placeholder {
  color: #64748b;
}

.input-wrapper input:focus {
  border-color: #00aac3;
  background: rgba(0, 170, 195, 0.05);
  box-shadow: 0 0 0 4px rgba(0, 170, 195, 0.1);
}

.password-toggle {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #00aac3;
}

/* Form Options */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #94a3b8;
  font-size: 0.9rem;
}

.remember-me input {
  display: none;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  position: relative;
  transition: all 0.2s ease;
}

.remember-me input:checked + .checkmark {
  background: #00aac3;
  border-color: #00aac3;
}

.remember-me input:checked + .checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Login Button */
.login-btn {
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #00aac3 0%, #0891b2 100%);
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.login-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0891b2 0%, #00aac3 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.login-btn:hover:not(:disabled)::before {
  opacity: 1;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 40px rgba(0, 170, 195, 0.4);
}

.login-btn:active:not(:disabled) {
  transform: translateY(0);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-btn span {
  position: relative;
  z-index: 1;
}

.loader-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Footer */
.form-footer {
  text-align: center;
  margin-top: auto;
  padding-top: 40px;
}

.form-footer p {
  color: #64748b;
  font-size: 0.85rem;
}

/* Responsive */
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    max-width: 450px;
    min-height: auto;
  }

  .branding-panel {
    padding: 40px 30px;
  }

  .brand-title {
    font-size: 1.8rem;
  }

  .features {
    display: none;
  }

  .form-panel {
    padding: 40px 30px;
  }

  .form-header h2 {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .login-container {
    margin: 10px;
    border-radius: 16px;
  }

  .branding-panel {
    padding: 30px 20px;
  }

  .logo {
    width: 60px;
  }

  .brand-title {
    font-size: 1.5rem;
  }

  .brand-tagline {
    font-size: 0.9rem;
    margin-bottom: 0;
  }

  .form-panel {
    padding: 30px 24px;
  }
}
</style>
